<template>
    <Teleport to="body">
        <div
            v-if="props.open"
            ref="floating"
            class="spark-popover"
            :style="popoverStyles"
        >
            <div
                ref="floatingArrow"
                class="spark-popover-arrow"
                :style="arrowStyle"
            />
            <div class="spark-popover-content">
                <slot :close="close" />
            </div>
        </div>
    </Teleport>
</template>

<script lang="ts" setup>
import {
    computed, onBeforeUnmount, ref, StyleValue, toRef, watch,
} from 'vue';
import {
    useFloating, autoUpdate, offset, flip, shift, arrow, type Placement,
} from '@floating-ui/vue';
import { usePrimeVue } from 'primevue/config';
import { useUIStore } from '@/stores/uiStore.ts';

const uiStore = useUIStore();

const props = withDefaults(defineProps<{
    open: boolean;
    reference: HTMLElement | null;
    placement?: Placement;
    isModal?: boolean;
}>(), {
    placement: 'bottom-start',
    isModal: false,
});

const emit = defineEmits<{
    'update:open': [boolean];
}>();

const floating = ref<HTMLElement | null>(null);
const floatingArrow = ref<HTMLElement | null>(null);
const primevue = usePrimeVue();

const NESTED_PRIME_OVERLAY_SELECTOR = [
    '.p-select-overlay',
    '.p-multiselect-overlay',
    '.p-datepicker-panel',
    '.p-popover',
    '.p-tieredmenu-root',
    '.p-menu-overlay',
].join(', ');

const floatingMiddleware = [
    offset(8),
    flip({ fallbackPlacements: ['top-start', 'bottom-start'] }),
    shift({ padding: 8 }),
    arrow({ element: floatingArrow }),
];

const {
    floatingStyles, placement, middlewareData, isPositioned,
} = useFloating(toRef(props, 'reference'), floating, {
    open: toRef(props, 'open'),
    placement: toRef(props, 'placement'),
    whileElementsMounted: autoUpdate,
    middleware: floatingMiddleware,
});

const popoverStyles = computed<StyleValue>(() => {
    let zIndex = primevue.config.zIndex?.overlay ?? 1000;
    if (props.isModal) zIndex = parseInt(uiStore.cssVariables.popoverOnModalZIndex, 10);

    return {
        ...floatingStyles.value,
        visibility: isPositioned.value ? 'visible' : 'hidden',
        zIndex,
    };
});

const arrowStyle = computed<StyleValue>(() => {
    if (!isPositioned.value) return { visibility: 'hidden' };

    if (!middlewareData.value.arrow) return {};

    const { x, y } = middlewareData.value.arrow;
    const side = placement.value.split('-')[0];

    const staticSide = {
        top: 'bottom',
        right: 'left',
        bottom: 'top',
        left: 'right',
    }[side] as string;

    const rotation = {
        top: 45,
        right: 135,
        bottom: -135,
        left: -45,
    }[side];

    return {
        left: x != null ? `${x}px` : '',
        top: y != null ? `${y}px` : '',
        [staticSide]: '-5.5px',
        transform: `rotate(${rotation}deg)`,
    };
});

let outsidePointerDownHandler: ((event: PointerEvent) => void) | null = null;
let escapeKeydownHandler: ((event: KeyboardEvent) => void) | null = null;

function isInsideNestedPrimeOverlay (target: Node): boolean {
    if (!(target instanceof Element)) return false;
    return !!target.closest(NESTED_PRIME_OVERLAY_SELECTOR);
}

function close (): void {
    emit('update:open', false);
}

function addOutsideCloseListener (): void {
    if (outsidePointerDownHandler) return;

    outsidePointerDownHandler = (event: PointerEvent): void => {
        if (!props.open) return;

        const target = event.target as Node | null;
        if (!target) return;
        if (floating.value?.contains(target)) return;
        if (props.reference?.contains(target)) return;
        if (isInsideNestedPrimeOverlay(target)) return;

        close();
    };

    window.addEventListener('pointerdown', outsidePointerDownHandler, true);
}

function removeOutsideCloseListener (): void {
    if (!outsidePointerDownHandler) return;
    window.removeEventListener('pointerdown', outsidePointerDownHandler, true);
    outsidePointerDownHandler = null;
}

function addEscapeListener (): void {
    if (escapeKeydownHandler) return;

    escapeKeydownHandler = (event: KeyboardEvent): void => {
        if (event.code !== 'Escape' || !props.open) return;
        close();
    };

    window.addEventListener('keydown', escapeKeydownHandler);
}

function removeEscapeListener (): void {
    if (!escapeKeydownHandler) return;
    window.removeEventListener('keydown', escapeKeydownHandler);
    escapeKeydownHandler = null;
}

watch(() => props.open, (isOpen) => {
    if (isOpen) {
        addOutsideCloseListener();
        addEscapeListener();
        return;
    }

    removeOutsideCloseListener();
    removeEscapeListener();
});

onBeforeUnmount(() => {
    removeOutsideCloseListener();
    removeEscapeListener();
});

</script>

<style lang="scss" scoped>
.spark-popover {
    background: var(--p-drawer-background);
    border: 1px solid var(--neutral300);
    border-radius: var(--borderRadiusOuter);
    box-shadow: var(--shadow3);
    transition: none;
}

.spark-popover-content {
    position: relative;
    z-index: 1;
}

.spark-popover-arrow {
    position: absolute;
    width: var(--defaultSpacing);
    height: var(--defaultSpacing);
    z-index: 0;
    background: var(--p-drawer-background);
    border-bottom: 1px solid var(--neutral300);
    border-right: 1px solid var(--neutral300);
    border-top: none;
    border-left: none;
}
</style>
